import { createFileRoute } from "@tanstack/react-router"
import { useRef } from "react"
import * as z from "zod"

import { generateMetaTags } from "@repo/seo/generate-meta-tags"
import { If } from "@repo/ui/components/ui/if"
import { pick } from "@repo/utils/collections"
import { sharedConfig } from "~/lib/config/shared"
import { AuthLayout } from "~/modules/auth/components/auth-layout"
import { OtpForm, type OtpFormRef } from "./otp-form"
import { RESEND_TYPES, ResendOtp, type ResendOtpProps } from "./resend-otp"

const searchSchema = z.looseObject({
  email: z.email().catch(""),
  type: z
    .enum([
      "email",
      "recovery",
      "invite",
      "email_change",
      "signup",
      "magiclink",
    ])
    .catch("email"),
  token: z.coerce
    .string()
    .refine((token) => token !== "undefined")
    .catch(""),
  next: z.string().optional().catch(""),
})

export const Route = createFileRoute("/_auth-layout/auth/otp")({
  component: OtpScreen,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => pick(search, ["email", "type", "token", "next"]),
  head: ({ match }) => ({
    meta: generateMetaTags({
      robots: ["noindex", "nofollow", "noarchive"],
      base: {
        title: `Verify OTP | ${sharedConfig.appName}`,
        description: `Verify OTP for ${sharedConfig.appName}`,
        url: new URL(match.fullPath, sharedConfig.baseUrl).toString(),
      },
    }),
  }),
})

function OtpScreen() {
  const { email, type, token, next } = Route.useSearch()
  const otpFormRef = useRef<OtpFormRef>(undefined)

  // We only want to show the resend OTP button if the type is one of the allowed types
  let resendType: ResendOtpProps["type"] | null = type as ResendOtpProps["type"]

  // biome-ignore lint/suspicious/noExplicitAny: please Runtime check is safe
  if (!RESEND_TYPES.includes(resendType as any) || !email) {
    resendType = null
  }

  const handleResend = () => {
    otpFormRef.current?.clearForm()
  }

  return (
    <AuthLayout
      title="OTP Verification"
      footer={
        <If condition={resendType}>
          {(type) => (
            <ResendOtp
              email={email}
              type={type}
              next={next}
              onResend={handleResend}
            />
          )}
        </If>
      }
    >
      <OtpForm
        ref={otpFormRef}
        token={token}
        email={email}
        type={type}
        next={next}
      />
    </AuthLayout>
  )
}
